import { Controller } from "@hotwired/stimulus";

declare global {
  interface Window {
    umami?: {
      track: (event: string, data?: Record<string, unknown>) => void;
    };
  }
}

// Tile click handler for the playground history peek strip. Intercepts plain
// clicks to open the expand modal in-place. Modifier-clicks (cmd/ctrl/shift,
// middle-click) fall through so the underlying restore URL still opens.
export default class extends Controller<HTMLAnchorElement> {
  static values = {
    sessionKey: String,
  };

  declare readonly sessionKeyValue: string;

  expand(event: MouseEvent): void {
    if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
      // Modifier-click → opens in new tab / window. The user is restoring,
      // just not in the current tab.
      this.trackRestore("tile-modifier");
      return;
    }
    if (event.button !== 0) return;

    // No template (e.g. an optimistic tile mid-generation) → fall through to
    // the underlying restore URL instead of intercepting with a no-op modal.
    const template = document.getElementById(
      `history_modal_template_${this.sessionKeyValue}`,
    );
    if (!template) {
      this.trackRestore("tile-direct");
      return;
    }

    const modalRoot = document.getElementById("history_expand_modal_root");
    if (!modalRoot) return;

    event.preventDefault();
    modalRoot.dispatchEvent(
      new CustomEvent("history-expand-modal:open", {
        detail: { sessionKey: this.sessionKeyValue, sourceTile: this.element },
      }),
    );
  }

  private trackRestore(via: string): void {
    try {
      const mode = document.getElementById("playground_history_strip")?.dataset.mode;
      window.umami?.track("playground-history-restored", { via, mode });
    } catch {
      // Telemetry is non-blocking — never let a track failure interrupt the
      // tile-click flow.
    }
  }
}
